import {EmptySheetRange} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/EmptySheetRange';
import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {SheetRangeImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeImpl';
import {SheetRangeListImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeListImpl';
import {
    INamingContext,
    ISheetContext,
    ISheetLocation,
    ISheetRange,
    ISheetRangeList,
    ISheetValue,
    SheetType,
    SheetValueType
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {FrameData} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';

export class FormulaValueCoercionUtils
{
    static ConvertToIValue(sheetContext:ISheetContext, namingContext:INamingContext, value:IValue | ISheetValue):IValue | IValue[] | AError
    {
        if( Type.isInstanceOf(value, Type.Error) )
            return value as IValue;

        if( Type.isInstanceOfIValue(value) )
            return value as IValue;

        if( SheetValueType.isInstanceOf(value, SheetType.SheetLocation) )
            return this.SheetLocationToIValue(sheetContext, namingContext, value as ISheetLocation);

        if( SheetValueType.isInstanceOf(value, SheetType.SheetRange) )
            return this.SheetRangeToIValue(sheetContext, namingContext, value as ISheetRange);

        if( SheetValueType.isInstanceOf(value, SheetType.SheetRangeList) )
            return this.SheetRangeListToIValue(sheetContext, namingContext, value as ISheetRangeList);

        return EvalErrors.Value;
    }

    static SheetLocationToIValue(sheetContext:ISheetContext, namingContext:INamingContext, location:ISheetLocation):IValue
    {
        if( !location )
        {
            mk.warn(area.FormulaEval, `SheetLocationToIValue: location is null`);
            return EvalErrors.Value;
        }

        if( !sheetContext.isSheetExists(location.sheetId) )
        {
            mk.warn(area.FormulaEval, `SheetLocationToIValue: sheet is not found by sheetId=${location.sheetId}`);
            return EvalErrors.SheetRef;
        }

        if( location.columnIndex == -1 || location.rowIndex == -1 )
        {
            const range = this.ConvertLocationToSheetRange(sheetContext, location);

            if( Type.isInstanceOf(range, Type.Error) )
                return range as AError;

            return this.SheetRangeToIValue(sheetContext, namingContext, range as ISheetRange);
        }

        let cellValue = sheetContext.getCellValue(location);
        if( !cellValue )
        {
            mk.warn(area.FormulaEval, `SheetLocationToIValue: ${location} => EMPTY`);
            return AEmpty.Value;
        }

        // Performance: Frame serialization is slow and has a hit on performance
        // mk.debug(area.FormulaEval, `SheetLocationToIValue: ${location} => ${cellValue}`);

        return cellValue;
    }

    static SheetRangeToIValue(sheetContext:ISheetContext, namingContext:INamingContext, range:ISheetRange):IValue
    {
        if( !range )
        {
            mk.warn(area.FormulaEval, `SheetRangeToIValue: range is null`);
            return EvalErrors.Value;
        }

        if( range === EmptySheetRange )
        {
            mk.warn(area.FormulaEval, `SheetRangeToIValue: range is empty`);
            return AFrame.empty();
        }

        const result = sheetContext.getRangeValue(range);

        /*
				const numCols = Math.abs(range.to.columnIndex - range.from.columnIndex) + 1;

				const fields:FrameField[] = [];
				for( let i = 0; i < numCols; i++ )
					fields.push(new FrameField(namingContext.getNextColumnName(), Type.String));

				const result = Frames.fromFieldsAndData(fields, FormulaValueCoercionUtils.ReadRangeFromSheet(sheetContext, range));
		*/

        // Performance: Frame serialization is slow and has a hit on performance
        //mk.debug(area.Formula, `SheetRangeToIValue: ${result}`);

        return result;
    }

    static SheetRangeListToIValue(sheetContext:ISheetContext, namingContext:INamingContext, rangeList:ISheetRangeList):IValue[] | AError
    {
        if( !rangeList )
        {
            mk.warn(area.FormulaEval, `SheetRangeToIValue: range list is null`);
            return EvalErrors.Value;
        }

        if( rangeList.ranges.length === 0 )
        {
            mk.warn(area.FormulaEval, `SheetRangeToIValue: range list is empty`);
            return [];
        }

        const result = [];

        for( let i = 0; i < rangeList.ranges.length; i++ )
            result[i] = this.SheetRangeToIValue(sheetContext, namingContext, rangeList.ranges[i]);

        // Performance: Frame serialization is slow and has a hit on performance
        //mk.debug(area.Formula, `SheetRangeToIValue: ${result}`);

        return result;
    }

    static PrintValues(context:ISheetContext, values:(IValue | ISheetValue)[]):string
    {
        return values.map((v:IValue | ISheetValue) =>
        {
            if( Type.isInstanceOfIValue(v as any) )
                return `${(v as IValue).getType().kind}:${v.toString()}`;
            else
                return `${v}`;

        }).join(', ');
    }

    static ConvertToSheetLocation(sheetContext:ISheetContext, value:IValue | ISheetValue):ISheetLocation | AError
    {
        let result:ISheetLocation | AError = EvalErrors.InvalidOperands;

        if( SheetValueType.isInstanceOfSheetValue(value) )
        {
            const sheetValue = value as ISheetValue;
            const sheetType = sheetValue.getType();

            if( sheetType === SheetType.SheetRange )
            {
                const range = value as ISheetRange;
                if( range && range.from )
                    result = range.from;
            }
            else if( sheetType === SheetType.SheetLocation )
            {
                const location = value as ISheetLocation;
                if( location )
                    result = location;
            }
        }

        // Performance: Frame serialization is slow and has a hit on performance
        //mk.debug(area.Formula, `ConvertToSheetLocation: ${value} => ${result}`);

        return result;
    }

    static ConvertToSheetRange(sheetContext:ISheetContext, value:IValue | ISheetValue):ISheetRange | AError
    {
        let result:ISheetRange | AError = EvalErrors.InvalidOperands;

        if( SheetValueType.isInstanceOfSheetValue(value) )
        {
            const sheetValue = value as ISheetValue;
            const sheetType = sheetValue.getType();

            if( sheetType === SheetType.SheetRange )
            {
                const range = value as ISheetRange;
                if( range )
                    result = range;
            }
            else if( sheetType === SheetType.SheetLocation )
            {
                result = this.ConvertLocationToSheetRange(sheetContext, value as ISheetLocation);
            }
        }

        // Performance: Frame serialization is slow and has a hit on performance
        //mk.debug(area.Formula, `ConvertToSheetRange: ${value} => ${result}`);

        return result;
    }

    static ConvertLocationToSheetRange(sheetContext:ISheetContext, location:ISheetLocation):ISheetRange | AError
    {
        if( location )
        {
            if( location.rowIndex == -1 )
            {
                if( location.columnIndex == -1 )
                    return EvalErrors.Ref;

                const from = new SheetLocationImpl(location.sheetId, location.columnIndex, 0);
                const to = new SheetLocationImpl(
                    location.sheetId,
                    location.columnIndex,
                    this.getLastRowIndex(sheetContext, location.sheetId)
                );
                return new SheetRangeImpl(location.sheetId, from, to);
            }
            else if( location.columnIndex == -1 )
            {
                if( location.rowIndex == -1 )
                    return EvalErrors.Ref;

                const from = new SheetLocationImpl(location.sheetId, 0, location.rowIndex);
                const to = new SheetLocationImpl(
                    location.sheetId,
                    this.getLastColumnIndex(sheetContext, location.sheetId),
                    location.rowIndex
                );
                return new SheetRangeImpl(location.sheetId, from, to);
            }
            else
                return new SheetRangeImpl(location.sheetId, location, location);
        }

        return EvalErrors.Ref;
    }

    static ConvertToSheetRangeList(sheetContext:ISheetContext, value:IValue | ISheetValue):ISheetRangeList | AError
    {
        let result:ISheetRangeList | AError = EvalErrors.InvalidOperands;

        if( Type.isInstanceOf(value, Type.Error) )
            return value as AError;

        if( SheetValueType.isInstanceOfSheetValue(value) )
        {
            const sheetValue = value as ISheetValue;
            const sheetType = sheetValue.getType();

            if( sheetType === SheetType.SheetRangeList )
            {
                const rangeList = value as ISheetRangeList;
                if( rangeList )
                    result = rangeList;
            }
            else if( sheetType === SheetType.SheetRange )
            {
                const range = value as ISheetRange;
                if( range )
                    result = new SheetRangeListImpl([range]);
            }
            else if( sheetType === SheetType.SheetLocation )
            {
                const location = value as ISheetLocation;
                if( location )
                {
                    const sheetRange = this.ConvertLocationToSheetRange(sheetContext, value as ISheetLocation);

                    if( Type.isInstanceOf(sheetType, Type.Error) )
                        return sheetRange as AError;

                    result = new SheetRangeListImpl([sheetRange as ISheetRange]);
                }
            }
        }

        // Performance: Frame serialization is slow and has a hit on performance
        //mk.debug(area.Formula, `ConvertToSheetRange: ${value} => ${result}`);

        return result;
    }

    static ConvertRangeListToFrame(sheetContext:ISheetContext, namingContext:INamingContext, sheetValue:ISheetValue):AFrame | AError
    {
        const rangeList = FormulaValueCoercionUtils.ConvertToSheetRangeList(sheetContext, sheetValue);
        if( SheetValueType.isInstanceOf(rangeList, SheetType.SheetRangeList) )
        {
            let result:AFrame = null;

            const sheetRanges = (<ISheetRangeList>rangeList).ranges;

            for( let i = 0; i < sheetRanges.length; i++ )
            {
                const sheetRange = sheetRanges[i];

                const frame = FormulaValueCoercionUtils.ConvertToIValue(sheetContext, namingContext, sheetRange);
                if( Type.isInstanceOf(frame, Type.Frame) )
                {
                    result = result ? result.extendWith((<AFrame>frame)) as AFrame : (<AFrame>frame);
                }
            }

            if( result )
            {
                return result;
            }
        }

        return EvalErrors.Error;
    }

    static CreateFrameFromRows(sheetContext:ISheetContext, namingContext:INamingContext, rows:(IValue | ISheetValue)[][]):AFrame | AError
    {
        let maxLength = 0;
        rows.forEach(row => maxLength = Math.max(maxLength, row.length));

        if( maxLength == 0 )
            return new AFrame([], []);

        const fields:Array<FrameField> = new Array<FrameField>();
        for( let i = 1; i <= maxLength; i++ )
        {
            fields.push(new FrameField(namingContext.getNextColumnName(), Type.String));
        }

        const data:FrameData = new Array<Array<IValue>>();

        for( let i = 0; i < rows.length; i++ )
        {
            const rowData:Array<IValue> = new Array<IValue>();
            const rowValues:(IValue | ISheetValue)[] = rows[i];

            for( let j = 0; j < maxLength; j++ )
            {
                if( j >= rowValues.length )
                {
                    rowData.push(AEmpty.Value);
                }
                else
                {
                    const value = FormulaValueCoercionUtils.ConvertToIValue(sheetContext, namingContext, rowValues[j]);
                    if( Array.isArray(value) )
                    {
                        rowData.push(FormulaValueCoercionUtils.CreateFrameFromRows(sheetContext, namingContext, value as any));
                    }
                    else
                        rowData.push(value);
                }
            }

            data.push(rowData);
        }

        return new AFrame(fields, data);
    }

    static getLastColumnIndex(sheetContext:ISheetContext, sheetId:SheetId):number
    {
        try
        {
            return sheetContext.getColumnsCount(sheetId) - 1;
        }
        catch( e )
        {
            mk.error(area.FormulaEval, e);
        }

        return 0;
    }

    static getLastRowIndex(sheetContext:ISheetContext, sheetId:SheetId):number
    {
        try
        {
            return sheetContext.getRowsCount(sheetId) - 1;
        }
        catch( e )
        {
            mk.error(area.FormulaEval, e);
        }

        return 0;
    }
}
